import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  CheckCircle2, XCircle, AlertCircle, MinusCircle, RefreshCw,
  Wifi, Server, Truck, Shield, Clock, Activity, Play, Square, Send, Ban, Loader2, PackageCheck
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const StatusBadge = ({ status }) => {
  const config = {
    OK: { className: "bg-green-500 hover:bg-green-600 text-white", icon: CheckCircle2 },
    PENDENTE: { className: "bg-yellow-500 hover:bg-yellow-600 text-white", icon: AlertCircle },
    ERRO: { className: "bg-red-500 text-white", icon: XCircle },
    "N/A": { className: "bg-gray-200 text-gray-600", icon: MinusCircle },
  };
  const { className, icon: Icon } = config[status] || config["PENDENTE"];
  return (
    <Badge className={`${className} flex items-center gap-1`}>
      <Icon className="h-3 w-3" />{status}
    </Badge>
  );
};

const CheckItemRow = ({ check }) => (
  <div className="flex flex-col sm:flex-row sm:items-center justify-between py-3 border-b last:border-0 gap-2">
    <div className="flex-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium text-sm sm:text-base">{check.name}</span>
        {check.required && <Badge variant="outline" className="text-xs">Obrigatório</Badge>}
      </div>
      <p className="text-xs sm:text-sm text-muted-foreground mt-1">{check.message}</p>
    </div>
    <StatusBadge status={check.status} />
  </div>
);

export function KeetaHomologationTab() {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [events, setEvents] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [connectivityResult, setConnectivityResult] = useState(null);
  const [error, setError] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState("status");

  // Estados para teste de status
  const [testOrderId, setTestOrderId] = useState("");
  const [cancelReason, setCancelReason] = useState("INTERNAL_ERROR");
  const [testLoading, setTestLoading] = useState({});
  const [testResults, setTestResults] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);

  const getAuthHeaders = () => ({
    "Content-Type": "application/json",
    "Authorization": `Bearer ${localStorage.getItem("token")}`
  });

  const loadHomologationStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/keeta/homologation/status`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error("Falha ao carregar status");
      setReport(await res.json());
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, []);

  const loadDeliveryScenario = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/keeta/homologation/delivery-scenario`, { headers: getAuthHeaders() });
      if (res.ok) setScenario(await res.json());
    } catch (err) { console.error(err); }
  }, []);

  const loadEvents = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/keeta/homologation/events`, { headers: getAuthHeaders() });
      if (res.ok) setEvents(await res.json());
    } catch (err) { console.error(err); }
  }, []);

  const loadEndpoints = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/keeta/homologation/endpoints`, { headers: getAuthHeaders() });
      if (res.ok) setEndpoints(await res.json());
    } catch (err) { console.error(err); }
  }, []);

  const loadRecentOrders = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/keeta/homologation/orders/recent?limit=5`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setRecentOrders(data.orders || []); console.log("[KEETA] Pedidos recentes:", data.orders);
      }
    } catch (err) { console.error(err); }
  }, []);

  const testConnectivity = async () => {
    setConnectivityResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/keeta/homologation/test-connectivity`, { method: "POST", headers: getAuthHeaders() });
      setConnectivityResult(await res.json());
    } catch (err) { setConnectivityResult({ success: false, message: err.message }); }
  };

  const revalidate = async () => {
    setLoading(true);
    try {
      await fetch(`${API_BASE}/api/keeta/homologation/revalidate`, { method: "POST", headers: getAuthHeaders() });
      await loadHomologationStatus();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  // ========== FUNÇÕES DE TESTE DE STATUS ==========
  const executeStatusTest = async (action, orderId, extraBody = {}) => {
    if (!orderId) {
      alert("Informe o Order ID");
      return;
    }
    setTestLoading(prev => ({ ...prev, [action]: true }));
    try {
      const url = `${API_BASE}/api/keeta/homologation/orders/${orderId}/${action}`;
      const res = await fetch(url, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ order_id: orderId, ...extraBody })
      });
      const result = await res.json();
      setTestResults(prev => [{
        action,
        orderId,
        ...result,
        executedAt: new Date().toLocaleString("pt-BR")
      }, ...prev.slice(0, 9)]);
      // Atualizar lista de pedidos após ação
      loadRecentOrders();
    } catch (err) {
      setTestResults(prev => [{
        action,
        orderId,
        success: false,
        message: err.message,
        executedAt: new Date().toLocaleString("pt-BR")
      }, ...prev.slice(0, 9)]);
    } finally {
      setTestLoading(prev => ({ ...prev, [action]: false }));
    }
  };

  const handleConfirm = () => executeStatusTest("confirm", testOrderId);
  const handleReady = () => executeStatusTest("ready", testOrderId);
  const handleDispatch = () => executeStatusTest("dispatch", testOrderId);
  const handleDelivered = () => executeStatusTest("delivered", testOrderId);
  const handleCancel = () => executeStatusTest("cancel", testOrderId, { cancellation_reason: cancelReason });

  useEffect(() => {
    loadHomologationStatus();
    loadDeliveryScenario();
    loadEvents();
    loadEndpoints();
    loadRecentOrders();
  }, [loadHomologationStatus, loadDeliveryScenario, loadEvents, loadEndpoints, loadRecentOrders]);

  const configChecks = report?.checks?.filter(c => ["credentials","merchant_mapping","access_token","onboarding","webhook_config"].includes(c.id)) || [];
  const logisticChecks = report?.checks?.filter(c => ["batch_decrypt","dispatch","store_toggle"].includes(c.id)) || [];

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-0">
      {/* Status Card */}
      <Card>
        <CardHeader className="pb-3 px-3 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Shield className="h-4 w-4 sm:h-5 sm:w-5" />Status de Homologação
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">Diagnóstico completo da integração Keeta</CardDescription>
            </div>
            <Button onClick={revalidate} disabled={loading} variant="outline" size="sm" className="w-full sm:w-auto">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />Revalidar
            </Button>
          </div>
        </CardHeader>
        <CardContent className="px-3 sm:px-6">
          {error && <Alert variant="destructive" className="mb-4"><AlertCircle className="h-4 w-4" /><AlertTitle>Erro</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>}
          {report && (
            <>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-4">
                <div className="text-center p-2 sm:p-4 bg-muted rounded-lg">
                  <div className="text-lg sm:text-2xl font-bold text-green-600">{report.total_ok}</div>
                  <div className="text-xs sm:text-sm text-muted-foreground">OK</div>
                </div>
                <div className="text-center p-2 sm:p-4 bg-muted rounded-lg">
                  <div className="text-lg sm:text-2xl font-bold text-yellow-600">{report.total_pending}</div>
                  <div className="text-xs sm:text-sm text-muted-foreground">Pendentes</div>
                </div>
                <div className="text-center p-2 sm:p-4 bg-muted rounded-lg">
                  <div className="text-lg sm:text-2xl font-bold text-red-600">{report.total_error}</div>
                  <div className="text-xs sm:text-sm text-muted-foreground">Erros</div>
                </div>
                <div className="text-center p-2 sm:p-4 bg-muted rounded-lg hidden sm:block">
                  <div className="text-lg sm:text-2xl font-bold text-gray-600">{report.total_na}</div>
                  <div className="text-xs sm:text-sm text-muted-foreground">N/A</div>
                </div>
                <div className="text-center p-2 sm:p-4 bg-muted rounded-lg hidden sm:block">
                  {report.ready_for_production ? <CheckCircle2 className="h-6 w-6 sm:h-8 sm:w-8 text-green-600 mx-auto" /> : <XCircle className="h-6 w-6 sm:h-8 sm:w-8 text-red-600 mx-auto" />}
                  <div className="text-xs sm:text-sm text-muted-foreground mt-1">{report.ready_for_production ? "Pronto" : "Não Pronto"}</div>
                </div>
              </div>
              <Alert className={`mt-4 ${report.ready_for_production ? "border-green-500" : "border-yellow-500"}`}>
                <Activity className="h-4 w-4" />
                <AlertTitle className="text-sm">Resumo</AlertTitle>
                <AlertDescription className="text-xs sm:text-sm">{report.summary}</AlertDescription>
              </Alert>
            </>
          )}
        </CardContent>
      </Card>

      {/* Cenário Logístico - Mobile Friendly */}
      {scenario && (
        <Card>
          <CardHeader className="pb-3 px-3 sm:px-6">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Truck className="h-4 w-4 sm:h-5 sm:w-5" />Cenário Logístico
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <div className="font-semibold text-sm sm:text-lg">{scenario.name}</div>
                <p className="text-xs sm:text-sm text-muted-foreground">{scenario.description}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Badge variant={scenario.batch_decrypt_required ? "default" : "outline"} className="text-xs">
                  Decrypt: {scenario.batch_decrypt_required ? "Sim" : "N/A"}
                </Badge>
                <Badge variant={scenario.dispatch_required ? "default" : "outline"} className="text-xs">
                  Dispatch: {scenario.dispatch_required ? "Sim" : "N/A"}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs - Responsivo */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className="grid w-full grid-cols-3 sm:grid-cols-6 h-auto">
          <TabsTrigger value="status" className="text-xs sm:text-sm py-2">
            <Server className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
            <span className="hidden sm:inline">Config</span>
          </TabsTrigger>
          <TabsTrigger value="test-status" className="text-xs sm:text-sm py-2">
            <Play className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
            <span className="hidden sm:inline">Teste</span>
          </TabsTrigger>
          <TabsTrigger value="endpoints" className="text-xs sm:text-sm py-2">
            <Wifi className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
            <span className="hidden sm:inline">Endpoints</span>
          </TabsTrigger>
          <TabsTrigger value="events" className="text-xs sm:text-sm py-2">
            <Activity className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
            <span className="hidden sm:inline">Eventos</span>
          </TabsTrigger>
          <TabsTrigger value="logistics" className="text-xs sm:text-sm py-2">
            <Truck className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
            <span className="hidden sm:inline">Logística</span>
          </TabsTrigger>
          <TabsTrigger value="connectivity" className="text-xs sm:text-sm py-2">
            <Wifi className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
            <span className="hidden sm:inline">Conexão</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="status">
          <Card>
            <CardHeader className="px-3 sm:px-6">
              <CardTitle className="text-base sm:text-lg">Configuração</CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              {configChecks.map(c => <CheckItemRow key={c.id} check={c} />)}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="test-status">
          <Card>
            <CardHeader className="px-3 sm:px-6">
              <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                <Play className="h-4 w-4 sm:h-5 sm:w-5" />
                Teste de Status
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                Teste manual das transições de status na API Keeta
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 sm:space-y-6 px-3 sm:px-6">
              {/* Order ID Input */}
              <div className="space-y-2">
                <label className="text-xs sm:text-sm font-medium">Order ID (Keeta)</label>
                <div className="flex gap-2">
                  <Input 
                    placeholder="Ex: 12345678..."
                    value={testOrderId}
                    onChange={(e) => setTestOrderId(e.target.value)}
                    className="flex-1 text-sm"
                  />
                  <Button variant="outline" onClick={loadRecentOrders} size="icon" title="Atualizar pedidos">
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
                
                {/* Pedidos Recentes */}
                <div className="mt-2">
                  <p className="text-xs text-muted-foreground mb-2">Pedidos recentes:</p>
                  <div className="flex flex-wrap gap-1">
                    {recentOrders.length > 0 ? recentOrders.map(order => (
                      <Button
                        key={order.keeta_order_id}
                        variant={testOrderId === order.keeta_order_id ? "default" : "outline"}
                        size="sm"
                        className="text-xs h-7 px-2"
                        onClick={() => setTestOrderId(order.keeta_order_id)}
                      >
                        #{order.keeta_order_id?.slice(-6)} 
                        <Badge variant="secondary" className="ml-1 text-[10px] px-1">
                          {order.status}
                        </Badge>
                      </Button>
                    )) : <span className="text-xs text-muted-foreground">Clique em 🔄 para carregar</span>}
                  </div>
                </div>
              </div>

              {/* Motivo de Cancelamento */}
              <div className="space-y-2">
                <label className="text-xs sm:text-sm font-medium">Motivo de Cancelamento</label>
                <Select value={cancelReason} onValueChange={setCancelReason}>
                  <SelectTrigger className="w-full text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ITEM_UNAVAILABLE">Item Indisponível</SelectItem>
                    <SelectItem value="STORE_CLOSED">Loja Fechada</SelectItem>
                    <SelectItem value="DELIVERY_AREA">Área de Entrega</SelectItem>
                    <SelectItem value="INTERNAL_ERROR">Erro Interno</SelectItem>
                    <SelectItem value="CUSTOMER_REQUEST">Solicitação do Cliente</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Botões de Ação - Responsivo com 5 botões */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3">
                <Button 
                  onClick={handleConfirm} 
                  disabled={testLoading.confirm || !testOrderId} 
                  className="bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm h-10"
                >
                  {testLoading.confirm ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                  Confirmar
                </Button>
                <Button 
                  onClick={handleReady} 
                  disabled={testLoading.ready || !testOrderId} 
                  className="bg-orange-500 hover:bg-orange-600 text-xs sm:text-sm h-10"
                >
                  {testLoading.ready ? <Loader2 className="h-4 w-4 animate-spin" /> : <Square className="h-4 w-4 mr-1" />}
                  Pronto
                </Button>
                <Button 
                  onClick={handleDispatch} 
                  disabled={testLoading.dispatch || !testOrderId} 
                  className="bg-purple-600 hover:bg-purple-700 text-xs sm:text-sm h-10"
                >
                  {testLoading.dispatch ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                  Despachar
                </Button>
                <Button 
                  onClick={handleDelivered} 
                  disabled={testLoading.delivered || !testOrderId} 
                  className="bg-green-600 hover:bg-green-700 text-xs sm:text-sm h-10"
                >
                  {testLoading.delivered ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageCheck className="h-4 w-4 mr-1" />}
                  Entregue
                </Button>
                <Button 
                  onClick={handleCancel} 
                  disabled={testLoading.cancel || !testOrderId} 
                  variant="destructive"
                  className="text-xs sm:text-sm h-10 col-span-2 sm:col-span-1"
                >
                  {testLoading.cancel ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4 mr-1" />}
                  Cancelar
                </Button>
              </div>

              {/* Log de Testes */}
              {testResults.length > 0 && (
                <div className="mt-4 sm:mt-6">
                  <h4 className="font-semibold mb-2 sm:mb-3 text-sm sm:text-base">Log de Testes</h4>
                  <div className="space-y-2 max-h-60 sm:max-h-80 overflow-y-auto">
                    {testResults.map((result, idx) => (
                      <div key={idx} className={`p-2 sm:p-3 rounded-lg border ${result.success ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                        <div className="flex items-center justify-between flex-wrap gap-1">
                          <div className="flex items-center gap-1 sm:gap-2">
                            {result.success ? <CheckCircle2 className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" /> : <XCircle className="h-3 w-3 sm:h-4 sm:w-4 text-red-600" />}
                            <span className="font-medium capitalize text-xs sm:text-sm">{result.action}</span>
                            <Badge variant="outline" className="text-[10px] sm:text-xs">#{result.orderId?.slice(-6)}</Badge>
                          </div>
                          <span className="text-[10px] sm:text-xs text-muted-foreground">{result.executedAt}</span>
                        </div>
                        <p className="text-xs sm:text-sm mt-1">{result.message}</p>
                        {result.local_status && <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">Status: {result.local_status}</p>}
                        {result.keeta_response && (
                          <details className="mt-2">
                            <summary className="text-[10px] sm:text-xs cursor-pointer text-blue-600">Ver resposta Keeta</summary>
                            <pre className="text-[10px] sm:text-xs bg-slate-100 p-2 rounded mt-1 overflow-x-auto">{JSON.stringify(result.keeta_response, null, 2)}</pre>
                          </details>
                        )}
                        {result.error && <p className="text-[10px] sm:text-xs text-red-600 mt-1">Erro: {result.error}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="endpoints">
          <Card>
            <CardHeader className="px-3 sm:px-6">
              <CardTitle className="text-base sm:text-lg">Endpoints API</CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              <h4 className="font-semibold mb-2 text-sm">Obrigatórios</h4>
              {endpoints.filter(e => e.required).map(ep => (
                <div key={ep.id} className="flex items-center justify-between py-2 border-b">
                  <span className="text-xs sm:text-sm">{ep.name}</span>
                  <StatusBadge status={ep.implemented ? "OK" : "PENDENTE"} />
                </div>
              ))}
              <h4 className="font-semibold mt-4 mb-2 text-sm">Opcionais</h4>
              {endpoints.filter(e => !e.required).map(ep => (
                <div key={ep.id} className="flex items-center justify-between py-2 border-b">
                  <span className="text-xs sm:text-sm">{ep.name}</span>
                  <StatusBadge status={ep.implemented ? "OK" : "N/A"} />
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="events">
          <Card>
            <CardHeader className="px-3 sm:px-6">
              <CardTitle className="text-base sm:text-lg">Eventos</CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {events.map(evt => (
                  <div key={evt.id} className="flex items-center justify-between p-2 sm:p-3 border rounded-lg">
                    <span className="font-medium text-xs sm:text-sm">{evt.name}</span>
                    <StatusBadge status={evt.implemented ? "OK" : (evt.required ? "PENDENTE" : "N/A")} />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="logistics">
          <Card>
            <CardHeader className="px-3 sm:px-6">
              <CardTitle className="text-base sm:text-lg">Logística</CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              {logisticChecks.map(c => <CheckItemRow key={c.id} check={c} />)}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="connectivity">
          <Card>
            <CardHeader className="px-3 sm:px-6">
              <CardTitle className="text-base sm:text-lg">Conectividade</CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              <Button onClick={testConnectivity} variant="outline" size="sm">
                <Wifi className="h-4 w-4 mr-2" />Testar Conexão
              </Button>
              {connectivityResult && (
                <Alert className="mt-4" variant={connectivityResult.success ? "default" : "destructive"}>
                  {connectivityResult.success ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                  <AlertTitle className="text-sm">{connectivityResult.success ? "OK" : "Falha"}</AlertTitle>
                  <AlertDescription className="text-xs sm:text-sm">
                    {connectivityResult.message}
                    {connectivityResult.latency_ms && <span className="block">Latência: {connectivityResult.latency_ms}ms</span>}
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Timestamp */}
      {report && (
        <div className="text-[10px] sm:text-xs text-muted-foreground text-right flex items-center justify-end gap-1">
          <Clock className="h-3 w-3" />
          Última verificação: {new Date(report.generated_at).toLocaleString("pt-BR")}
        </div>
      )}
    </div>
  );
}

export default KeetaHomologationTab;
